/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import router from "next/navigation";
import React, { useContext, PropsWithChildren, ComponentType } from "react";
import type Lottie from "lottie-react";
import type { UrlObject } from "url";

import { CosmosImageReactElementType, DefaultImage } from "./DefaultImage";
import { CosmosLinkReactElementType, DefaultLink } from "./DefaultLink";
import type { LoadingProps } from "./DefaultLoading";
import type { PaddingBlockVariants } from "@components/utils/section.utils";

interface Pushable {
	push: (path: string | UrlObject) => void;
}

export interface CosmosComponentContextProps extends PropsWithChildren {
	Image: CosmosImageReactElementType;
	Link: CosmosLinkReactElementType;
	LoadingComponent?: ComponentType<LoadingProps>;
	Lottie?: typeof Lottie;
	feedBrand:
		"unreal-engine" | "eos" | "deveg" | "twinmotion" | "kws" | "mh" | "rs" | "eg" | "ue-editor" | "quixel" | "fab"; // from wf-services-src feed-service Brand
	feedRootPath: string;
	hygraphMediaDomain: string;
	router: Pushable | typeof router;
	enableAuth: boolean;
	locales: string[];
	paddingBlockVariant?: PaddingBlockVariants;
	maxWidth?: string;
	disableAnimations?: boolean;
	/** Browser-facing tenant origin for multi-site applications. */
	serverUrl?: string;
	siteBasePath?: string;
}

export const CosmosComponentContext = React.createContext<CosmosComponentContextProps>({
	Image: DefaultImage,
	Link: DefaultLink,
	LoadingComponent: undefined,
	Lottie: undefined,
	feedBrand: "unreal-engine",
	feedRootPath: "/feed",
	hygraphMediaDomain: "cms-assets.unrealengine.com",
	router: {
		push: () => {
			console.log("CosmosComponentContext.router() default push handler ");
		},
	},
	enableAuth: true,
	locales: ["en-US"],
	paddingBlockVariant: "default",
	maxWidth: "1440px",
	disableAnimations: false,
	serverUrl: undefined,
	siteBasePath: undefined,
});

export const useCosmosComponentContext = (): CosmosComponentContextProps => {
	return useContext<CosmosComponentContextProps>(CosmosComponentContext);
};
